import React from 'react';
import styled from 'styled-components';
import MuiAccordion from '@material-ui/core/Accordion';
import MuiAccordionSummary from '@material-ui/core/AccordionSummary';
import MuiAccordionDetails from '@material-ui/core/AccordionDetails';
import SGTitle from 'components/Typography/SGTitle';
import Icon from '@mdi/react';
import SeverityBadge from 'features/POSRegisters/components/SeverityBadge';
import {mdiChevronUp, mdiChevronDown} from '@mdi/js';
import SeverityEnum from 'features/POSRegisters/interfaces/Severity';
import {AlertType} from 'features/POSRegisters/interfaces/AlertInfo';

//#region styles
const Accordion = styled(MuiAccordion)`
  width: inherit;
  background: ${props => props.theme.disabledStateBlue}33;
  box-shadow: none;
  padding: 0 8px;

  &.Mui-expanded {
    margin: 0;
    padding-bottom: 12px;
  }

  &.minor {
    background: ${props => props.theme.yellow}66;
  }

  &.major {
    background: ${props => props.theme.orange}4D;
  }

  &.critical {
    background: ${props => props.theme.red}4D;
  }

  &.scanfailure {
    background: ${props => props.theme.informationalBlue}66;
  }

  &.transparent {
    background: transparent;
  }
`;

const AccordionSummary = styled(MuiAccordionSummary)`
  min-height: 24px;
  padding: 0;
  align-items: center;

  &.Mui-expanded {
    min-height: 24px;
  }

  .MuiAccordionSummary-content {
    align-items: center;
    margin: 4px 0;
    gap: 8px;

    > svg {
      color: ${props => props.theme.text};
    }
  }
`;

const AccordionDetails = styled(MuiAccordionDetails)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0;
`;

const StyledTitle = styled(SGTitle)`
  text-transform: uppercase;
  line-height: 20px !important;
`;

const StyledArrowIcon = styled(Icon)``;
//#endregion styles

function ArrowIcon({expanded, placement}: {expanded: boolean; placement?: 'start' | 'end'}) {
  return (
    <StyledArrowIcon
      path={expanded ? mdiChevronUp : mdiChevronDown}
      style={{marginLeft: placement === 'end' ? 'auto' : ''}}
      size={1}
    />
  );
}

interface AccordionProps {
  id?: string;
  expanded?: boolean;
  icon?: React.ReactNode;
  title?: string;
  header?: React.ReactNode;
  type?: AlertType;
  severity?: SeverityEnum;
  arrowPlacement?: 'start' | 'end';
  children?: React.ReactNode;
  className?: string;
  onExpandChange?: (expanded: boolean) => void;
}

export default function CustomizedAccordions(props: AccordionProps) {
  const {
    type,
    icon,
    title,
    severity,
    header,
    children,
    className,
    arrowPlacement = 'end',
    onExpandChange,
  } = props;
  const [expanded, setExpanded] = React.useState<boolean>(props.expanded ?? false);

  const handleChange = (event: React.ChangeEvent<{}>, expanded: boolean) => {
    setExpanded(expanded);
    onExpandChange?.(expanded);
  };

  return (
    <Accordion
      id={props.id}
      className={`${severity?.toLowerCase()} ${className}`}
      expanded={expanded}
      onChange={handleChange}
      square
    >
      <AccordionSummary>
        {arrowPlacement === 'start' && <ArrowIcon expanded={expanded} />}
        {icon ? icon : severity && <SeverityBadge severity={severity} size={0.75} />}
        {title && (
          <StyledTitle variant="t6" className="text-color">
            {title}
          </StyledTitle>
        )}
        {header && header}
        {arrowPlacement === 'end' && <ArrowIcon placement={arrowPlacement} expanded={expanded} />}
      </AccordionSummary>
      <AccordionDetails>{children}</AccordionDetails>
    </Accordion>
  );
}
